import { useRef, useState } from "react";
import { Upload, X, Loader2, ImageIcon } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import ImageCropDialog from "./ImageCropDialog";

/**
 * Multi-image uploader for events/venues.
 * Props:
 *  - value: array of image URLs (controlled)
 *  - onChange: (urls) => void
 *  - max: max number of images (default 5)
 *  - presets: optional array of preset URLs to allow quick selection
 */
export default function ImageUploader({ value = [], onChange, max = 5, presets = [], aspect = 16 / 9 }) {
  const [uploading, setUploading] = useState(false);
  const [cropQueue, setCropQueue] = useState([]);
  const fileInputRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const remaining = max - value.length;

  const handleFiles = (files) => {
    const list = Array.from(files).slice(0, remaining);
    const valid = [];
    for (const file of list) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} n'est pas une image`);
        continue;
      }
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name} dépasse 8 Mo`);
        continue;
      }
      valid.push(file);
    }
    if (valid.length) setCropQueue((q) => [...q, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadCropped = async (file) => {
    setCropQueue((q) => q.slice(1));
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...value, `${BACKEND_URL}${data.url}`]);
      toast.success("Photo recadrée et ajoutée");
    } catch (e) {
      toast.error(`Échec upload ${file.name}: ${e.response?.data?.detail || e.message}`);
    }
    setUploading(false);
  };

  const removeAt = (i) => {
    onChange(value.filter((_, idx) => idx !== i));
  };

  const addPreset = (url) => {
    if (value.includes(url)) return;
    if (value.length >= max) {
      toast.error(`Maximum ${max} photos`);
      return;
    }
    onChange([...value, url]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid="image-uploader" className="space-y-4">
      {cropQueue.length > 0 && (
        <ImageCropDialog
          key={cropQueue[0].name + cropQueue[0].lastModified}
          file={cropQueue[0]}
          aspect={aspect}
          onCancel={() => setCropQueue((q) => q.slice(1))}
          onCropped={uploadCropped}
        />
      )}
      {/* Preview grid */}
      {value.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {value.map((url, i) => (
            <div key={url} className="group relative aspect-[4/3] border border-ink/10 overflow-hidden bg-cream-50">
              <img src={url} alt="" className="w-full h-full object-cover" />
              {i === 0 && (
                <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-[0.2em] bg-ink text-cream px-2 py-1">
                  Principale
                </span>
              )}
              <button
                type="button"
                data-testid={`remove-image-${i}`}
                onClick={() => removeAt(i)}
                className="absolute top-2 right-2 h-7 w-7 bg-white/95 hover:bg-white text-ink grid place-items-center transition-opacity opacity-0 group-hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload dropzone */}
      {remaining > 0 && (
        <label
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          className="block border border-dashed border-ink/25 hover:border-ink/50 hover:bg-cream-50 transition-colors cursor-pointer p-8 text-center"
          data-testid="upload-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            disabled={uploading}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-ink/60">
              <Loader2 className="h-5 w-5 animate-spin" />
              <p className="text-sm">Envoi en cours...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-ink/60">
              <Upload className="h-5 w-5" />
              <p className="text-sm">
                <span className="text-ink font-medium">Glissez vos photos</span> ou cliquez pour parcourir
              </p>
              <p className="text-[11px] text-ink/40 uppercase tracking-[0.2em]">
                {remaining} restante{remaining > 1 ? "s" : ""} · JPG/PNG · 8 Mo max · recadrage 16:9 à l'import
              </p>
            </div>
          )}
        </label>
      )}

      {/* Quick presets */}
      {presets.length > 0 && value.length < max && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-ink/40 mb-2 flex items-center gap-2">
            <ImageIcon className="h-3 w-3" /> Suggestions
          </p>
          <div className="grid grid-cols-4 gap-2">
            {presets.map((url) => {
              const selected = value.includes(url);
              return (
                <button
                  type="button"
                  key={url}
                  onClick={() => addPreset(url)}
                  disabled={selected}
                  className={`aspect-[4/3] overflow-hidden border transition-all ${
                    selected ? "opacity-40 cursor-not-allowed border-ink/10" : "border-ink/10 hover:border-accent"
                  }`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
